/**
 * ScrollForMoreOverlay threshold configuration
 * 
 * Maps page paths to their dvh threshold values.
 * If dvh is below the threshold on mobile, the "scroll for more" button will be hidden.
 * 
 * Thresholds can be:
 * - A fixed number (in pixels) - constant threshold
 * - An object with min/max values - fluid threshold that scales with viewport width
 * - null - show button regardless of dvh
 * 
 * For fluid thresholds, the value scales linearly between min (at 375px viewport) and max (at 1024px viewport).
 */
type ThresholdConfig = 
  | number // Fixed threshold
  | { min: number; max: number } // Fluid threshold (min at 375px, max at 1024px)
  | null; // No threshold

export const scrollForMoreThresholds: Record<string, ThresholdConfig> = {
  '/': null, // Homepage - no threshold
  '/about': { min: 580, max: 1208 }, // About page - fluid threshold
  '/what-we-do': null, // What We Do - no threshold (add value if needed)
  '/careers': null, // Careers - no threshold (add value if needed)
};

/**
 * Calculate fluid threshold value based on viewport width
 * 
 * @param min - Minimum threshold at 375px viewport
 * @param max - Maximum threshold at 1024px viewport
 * @param viewportWidth - Current viewport width
 * @returns Calculated threshold value
 */
function calculateFluidThreshold(min: number, max: number, viewportWidth: number): number {
  // Clamp viewport width between 375px and 1024px
  const clampedWidth = Math.max(375, Math.min(1024, viewportWidth));
  
  // Linear interpolation
  const ratio = (clampedWidth - 375) / (1024 - 375);
  return min + (max - min) * ratio;
}

/**
 * Get the minimum dvh threshold for a given page path
 * 
 * @param pathname - The current page pathname
 * @param viewportWidth - Current viewport width (for fluid thresholds)
 * @returns The threshold value in pixels, or null if no threshold is set
 */
export function getScrollForMoreThreshold(pathname: string, viewportWidth: number = 375): number | null {
  const threshold = scrollForMoreThresholds[pathname] ?? null;
  
  if (threshold === null) {
    return null;
  }
  
  if (typeof threshold === 'number') {
    return threshold;
  }
  
  // Fluid threshold
  return calculateFluidThreshold(threshold.min, threshold.max, viewportWidth);
}

